import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { flashcardApi, quizApi } from '../utils/api';
import ChessDiagram, { getPieceData } from '../components/ChessDiagram';
import {
  buildBoardSvg,
  parseFen,
  fetchPieceSet,
  DEFAULT_LIGHT_SQUARE,
  DEFAULT_DARK_SQUARE,
  DEFAULT_BORDER_COLOR,
} from './ChessDiagramGenerator';

// ── PDF helpers ────────────────────────────────────────────────────────────

/** Encode an SVG string as a base64 data URL. */
function svgToDataUrl(svgString) {
  return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgString)))}`;
}

/** Rasterise an SVG string to a PNG data URL via an off-screen canvas. */
function svgToPng(svgString) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth  || 280;
      const h = img.naturalHeight || 260;
      const scale = 2; // 2× for sharpness on screen and in print
      const canvas = document.createElement('canvas');
      canvas.width  = w * scale;
      canvas.height = h * scale;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve({ png: canvas.toDataURL('image/png'), w, h });
    };
    img.onerror = reject;
    img.src = svgToDataUrl(svgString);
  });
}

/**
 * Render "♚  Chess Flashcards" onto an off-screen canvas and return a PNG data URL.
 * Uses the browser's system font so the king glyph renders correctly.
 */
function renderHeaderPng() {
  const W = 500, H = 52, scale = 3;
  const canvas = document.createElement('canvas');
  canvas.width  = W * scale;
  canvas.height = H * scale;
  const ctx = canvas.getContext('2d');
  ctx.scale(scale, scale);
  ctx.clearRect(0, 0, W, H);
  ctx.font = 'bold 36px serif';
  ctx.fillStyle = getComputedStyle(document.documentElement).getPropertyValue('--theme-brand-nav').trim() || '#1c3a5c';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('♚  Chess Flashcards', W / 2, H / 2);
  return canvas.toDataURL('image/png');
}

/**
 * Generate and download a PDF of the report cards.
 * Layout mirrors the on-screen print grid: 2 columns × (perPage/2) rows per page.
 */
async function savePdf(cards, prefs, layout) {
  // Dynamic import so jspdf is not in the initial bundle
  const { jsPDF } = await import('jspdf');

  const perPage   = Number(layout); // 4, 6, 8, or 10
  const cols      = 2;
  const rowsPerPg = perPage / cols; // 2, 3, 4, or 5

  // A4 portrait dimensions (mm)
  const pageW     = 210;
  const pageH     = 297;
  const margin    = 10;   // left / right / bottom
  const topMargin = 1;    // top — shifted up ~9 mm (≈ 3/8 in) vs equal margins
  const headerH   = 16;   // mm reserved at top for logo + title
  const gapX      = 5;
  const gapY      = 5;

  const cellW = (pageW - 2 * margin - (cols - 1) * gapX) / cols;
  // cellH accounts for header band so cards don't overlap it
  const cellH = (pageH - topMargin - margin - headerH - (rowsPerPg - 1) * gapY) / rowsPerPg;

  // Cap diagram width so text still fits: 4-up generous, shrinks for denser layouts.
  const diagW = ({ 4: cellW - 4, 6: 55, 8: 45, 10: 38 })[perPage] ?? (cellW - 4);

  // Render header image once; reuse across all pages
  const headerPng   = renderHeaderPng();
  const headerImgW  = 80;  // mm
  const headerImgH  = headerImgW * (52 / 500); // preserve aspect ratio → ~8.3 mm

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  // Ensure the piece set used for on-screen rendering is available
  const setKey = prefs.diagramPieceSet ?? 'cburnett';
  let pieceData = getPieceData(setKey);
  if (!pieceData) {
    try {
      pieceData = await fetchPieceSet(setKey);
    } catch (e) {
      console.warn('PDF: could not fetch piece set, using unicode fallback');
      pieceData = null;
    }
  }

  for (let i = 0; i < cards.length; i++) {
    const col     = i % cols;
    const rowOnPg = Math.floor(i / cols) % rowsPerPg;

    // New page at the start of each full grid (except the very first card)
    if (i > 0 && col === 0 && rowOnPg === 0) doc.addPage();

    const cellX = margin + col * (cellW + gapX);
    const cellY = topMargin + headerH + rowOnPg * (cellH + gapY);

    // Cell border
    doc.setDrawColor(180);
    doc.setLineWidth(0.3);
    doc.rect(cellX, cellY, cellW, cellH);

    const card      = cards[i];
    const flipped   = card.flipped ?? prefs.diagramFlipped ?? false;
    const light     = prefs.diagramLightSquare   ?? DEFAULT_LIGHT_SQUARE;
    const dark      = prefs.diagramDarkSquare    ?? DEFAULT_DARK_SQUARE;
    const border    = prefs.diagramBorderColor   ?? DEFAULT_BORDER_COLOR;
    const coordPos  = prefs.diagramCoordPosition ?? 'border';

    const tight = perPage === 10;
    let curY = cellY + (tight ? 3 : 4);

    // ── Card name ────────────────────────────────────────────────────────
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    const nameLines = doc.splitTextToSize(card.name, cellW - 4);
    doc.text(nameLines, cellX + 2, curY);
    curY += nameLines.length * 3.8 + (tight ? 0 : 1);

    // ── Chess diagram ────────────────────────────────────────────────────
    if (card.fen) {
      try {
        const { board, activeColor } = parseFen(card.fen);
        const svgString = buildBoardSvg({
          board, activeColor, flipped,
          setKey,
          pieceData,
          boardSize:     240,
          lightSquare:   light,
          darkSquare:    dark,
          borderColor:   border,
          coordPosition: coordPos,
        });
        const { png, w, h } = await svgToPng(svgString);
        const diagH = diagW * (h / w); // preserve aspect ratio
        const imgX  = cellX + (cellW - diagW) / 2; // centered
        doc.addImage(png, 'PNG', imgX, curY, diagW, diagH);
        curY += diagH + (tight ? 1 : 2);
      } catch (e) {
        console.warn('PDF: diagram error for card', card.id, e);
      }
    }

    // ── Problem ──────────────────────────────────────────────────────────
    if (card.problem) {
      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'bold');
      doc.text('Problem:', cellX + 2, curY);
      curY += 3.2;
      doc.setFont('helvetica', 'normal');
      const remaining   = cellY + cellH - curY - 3;
      const maxProbLines = Math.max(1, Math.floor(remaining / 3));
      const probLines   = doc.splitTextToSize(card.problem, cellW - 4);
      doc.text(probLines.slice(0, maxProbLines), cellX + 2, curY);
      curY += Math.min(probLines.length, maxProbLines) * 3 + 1;
    }

  }

  // ── Stamp header + page number on every page ──────────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);

    // Header: logo + title centered at top
    const imgX = (pageW - headerImgW) / 2;
    const imgY = topMargin + (headerH - headerImgH) / 2 - 1;
    doc.addImage(headerPng, 'PNG', imgX, imgY, headerImgW, headerImgH);

    // Footer: sit just below the bottom border of the last card row
    const footerY = topMargin + headerH + rowsPerPg * cellH + (rowsPerPg - 1) * gapY + 4;
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(120);
    doc.text('www.chessflashcards.org', margin, footerY);
    doc.text(`Page ${p}`, pageW - margin, footerY, { align: 'right' });
    doc.setTextColor(0);
  }

  doc.save('quiz-report.pdf');
}

// ── Component ──────────────────────────────────────────────────────────────

export default function QuizReportPage() {
  const { token, prefs } = useAuth();

  const [allCards, setAllCards]           = useState([]);
  const [selected, setSelected]           = useState(new Set());
  const [layout, setLayout]               = useState('4');  // '4' or '6'
  const [reportCards, setReportCards]     = useState(null); // null = not yet generated
  const [error, setError]                 = useState('');
  const [loading, setLoading]             = useState(true);
  const [generating, setGenerating]       = useState(false);
  const [savingPdf, setSavingPdf]         = useState(false);

  useEffect(() => {
    flashcardApi.list(token)
      .then(data => setAllCards(data || []))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  const toggleCard = id => {
    setSelected(s => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === allCards.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(allCards.map(c => c.id)));
    }
  };

  const generate = useCallback(async () => {
    if (selected.size === 0) { alert('Select at least one card.'); return; }
    setGenerating(true);
    setError('');
    try {
      const ids   = Array.from(selected);
      const cards = await quizApi.report(ids, token);
      setReportCards(cards);
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  }, [selected, token]);

  const handleSavePdf = async () => {
    if (!reportCards) return;
    setSavingPdf(true);
    try {
      await savePdf(reportCards, prefs, layout);
    } catch (err) {
      alert('PDF export failed: ' + err.message);
    } finally {
      setSavingPdf(false);
    }
  };

  if (loading) return <div className="text-center mt-5"><div className="spinner-border" /></div>;

  // ── Print / PDF view ───────────────────────────────────────────────────────
  if (reportCards) {
    const perPage = Number(layout);
    const cols    = 2;
    const rows    = perPage / cols;

    return (
      <>
        <div className="no-print d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">{reportCards.length} cards — {layout}-up layout</h4>
          <div className="d-flex gap-2">
            <button className="btn btn-primary" onClick={() => window.print()}>
              🖨 Print
            </button>
            <button className="btn btn-outline-primary" onClick={handleSavePdf} disabled={savingPdf}>
              {savingPdf ? 'Generating PDF…' : '⬇ Save as PDF'}
            </button>
            <button className="btn btn-outline-secondary" onClick={() => setReportCards(null)}>
              ← Back
            </button>
          </div>
        </div>

        <div className={`print-grid print-grid-${layout}`}
          style={{ gridTemplateRows: `repeat(${rows}, auto)` }}>
          {reportCards.map(card => {
            const cardPrefs = { ...prefs, diagramFlipped: card.flipped ?? prefs.diagramFlipped };
            return (
              <div key={card.id} className="print-card">
                <h6>{card.name}</h6>
                {card.fen && (
                  <div style={{ lineHeight: 0, marginBottom: Number(layout) >= 8 ? 2 : layout === '6' ? 3 : 6 }}>
                    <ChessDiagram fen={card.fen} prefs={cardPrefs}
                      size={({'4':200,'6':110,'8':80,'10':65})[layout] ?? 80} />
                  </div>
                )}
                {card.problem  && <p className="mb-1"><strong>Problem:</strong> {card.problem}</p>}
              </div>
            );
          })}
        </div>
      </>
    );
  }

  // ── Selection view ─────────────────────────────────────────────────────────
  return (
    <>
      <h2 className="mb-1">Quiz Report</h2>
      <p className="text-muted mb-3">Select cards, choose a layout, then generate a printable report.</p>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Options */}
      <div className="card mb-3 shadow-sm">
        <div className="card-body d-flex flex-wrap gap-4 align-items-center">
          <div>
            <label className="form-label fw-semibold mb-1">Layout</label>
            <div className="d-flex gap-2">
              {['4', '6', '8', '10'].map(n => (
                <button key={n}
                  className={`btn btn-sm ${layout === n ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setLayout(n)}>
                  {n}-up
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Card selection */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <span className="small text-muted">{selected.size} of {allCards.length} selected</span>
        <button className="btn btn-sm btn-outline-secondary" onClick={toggleAll}>
          {selected.size === allCards.length ? 'Deselect All' : 'Select All'}
        </button>
      </div>

      <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-2 mb-4">
        {allCards.map(card => (
          <div className="col" key={card.id}>
            <div
              className={`card h-100 cursor-pointer ${selected.has(card.id) ? 'border-primary' : ''}`}
              style={{ cursor: 'pointer' }}
              onClick={() => toggleCard(card.id)}
            >
              <div className="card-body py-2 d-flex align-items-start gap-2">
                <input type="checkbox" className="form-check-input mt-1 flex-shrink-0"
                  checked={selected.has(card.id)}
                  onChange={() => toggleCard(card.id)}
                  onClick={e => e.stopPropagation()} />
                <div>
                  <div className="fw-semibold small">{card.name}</div>
                  {card.problem && (
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                      {card.problem.slice(0, 80)}{card.problem.length > 80 && '…'}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <button className="btn btn-primary btn-lg" onClick={generate}
        disabled={generating || selected.size === 0}>
        {generating ? 'Generating…' : `Generate ${layout}-up Report`}
      </button>
    </>
  );
}
